import { useCallback, useEffect, useState } from 'react'
import {
  DEFAULT_NAV,
  findSubNav,
  findTopNav,
  type SubNavId,
  type TopNavId,
} from './settings-tree'

const STORAGE_KEY = 'preferences.lastPath'

interface StoredNav {
  topNavId: TopNavId
  subNavId: SubNavId
}

interface NavState {
  topNavId: TopNavId
  subNavId: SubNavId
}

function readStored(): NavState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return { ...DEFAULT_NAV }
    const parsed = JSON.parse(raw) as Partial<StoredNav>
    if (!parsed || typeof parsed !== 'object') return { ...DEFAULT_NAV }
    // Validate against the tree — IDs we no longer ship must not crash the
    // shell. Falls back to default on any mismatch. The `find` helpers
    // accept `string` at the boundary, so untrusted localStorage input is
    // safe; the return value is narrowed back to the tight union.
    const tab = findTopNav(String(parsed.topNavId ?? ''))
    if (!tab) return { ...DEFAULT_NAV }
    const sub = findSubNav(tab.id, String(parsed.subNavId ?? ''))
    if (!sub) return { topNavId: tab.id, subNavId: tab.subNav[0].id }
    return { topNavId: tab.id, subNavId: sub.id }
  } catch {
    return { ...DEFAULT_NAV }
  }
}

function writeStored(state: NavState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch {
    // SecurityError in restricted contexts — silently skip; navigation
    // still works for the current session.
  }
}

/**
 * Owns the `{topNavId, subNavId}` pair for the Settings shell. Persists to
 * localStorage so the user lands on the same SubPage next time they open
 * Settings. Invalid stored values fall back to {@link DEFAULT_NAV}.
 *
 * Switching the TopNav auto-resets `subNavId` to the first SubNav of the
 * newly active tab — matches the Figma "Wechsel auf Hauptmenü öffnet
 * dessen erstes Untermenü" expectation.
 */
export function usePreferencesNavigation() {
  const [state, setState] = useState<NavState>(() => readStored())

  useEffect(() => {
    writeStored(state)
  }, [state])

  const setTopNav = useCallback((topNavId: TopNavId) => {
    setState((prev) => {
      if (prev.topNavId === topNavId) return prev
      const tab = findTopNav(topNavId)
      if (!tab) return prev
      return { topNavId: tab.id, subNavId: tab.subNav[0].id }
    })
  }, [])

  const setSubNav = useCallback((subNavId: SubNavId) => {
    setState((prev) => {
      if (prev.subNavId === subNavId) return prev
      const sub = findSubNav(prev.topNavId, subNavId)
      if (!sub) return prev
      return { ...prev, subNavId: sub.id }
    })
  }, [])

  return {
    topNavId: state.topNavId,
    subNavId: state.subNavId,
    setTopNav,
    setSubNav,
  }
}

// Exported for tests so they can clear stored state between runs.
export const PREFERENCES_NAV_STORAGE_KEY = STORAGE_KEY
